import { useCallback } from 'react';
import { create } from 'zustand';
const LessonPreQuizDefaultState = {
  LESSON: {}
};
const useLessonPreQuizStore = create(set => Object.assign({}, LessonPreQuizDefaultState, {
  initializeLessonPreQuiz: ({
    lessonId,
    totalQuestions,
    generationStatus
  }) => {
    set(state => {
      var _state$LESSON$lessonI, _state$LESSON$lessonI2, _state$LESSON$lessonI3;
      return {
        LESSON: Object.assign({}, state.LESSON, {
          [lessonId]: Object.assign({}, state.LESSON[lessonId], {
            questionIndex: 0,
            correctAnswers: 0,
            totalQuestions,
            perContentScores: {},
            tasks: ((_state$LESSON$lessonI = state.LESSON[lessonId]) === null || _state$LESSON$lessonI === void 0 ? void 0 : _state$LESSON$lessonI.tasks) || {},
            generationStatus,
            quizStarted: (_state$LESSON$lessonI2 = (_state$LESSON$lessonI3 = state.LESSON[lessonId]) === null || _state$LESSON$lessonI3 === void 0 ? void 0 : _state$LESSON$lessonI3.quizStarted) !== null && _state$LESSON$lessonI2 !== void 0 ? _state$LESSON$lessonI2 : false,
            isReviewingAnswer: false
          })
        })
      };
    });
  },
  incrementLessonPreQuizQuestion: ({
    correct,
    lessonId,
    lessonContentId
  }) => {
    set(state => {
      const current = state.LESSON[lessonId];
      if (!current) {
        return state;
      }
      const currentContentScore = current.perContentScores[lessonContentId] || {
        correct: 0,
        total: 0
      };
      return {
        LESSON: Object.assign({}, state.LESSON, {
          [lessonId]: Object.assign({}, current, {
            questionIndex: current.questionIndex + 1,
            correctAnswers: current.correctAnswers + (correct ? 1 : 0),
            isReviewingAnswer: true,
            perContentScores: Object.assign({}, current.perContentScores, {
              [lessonContentId]: {
                correct: currentContentScore.correct + (correct ? 1 : 0),
                total: currentContentScore.total + 1
              }
            })
          })
        })
      };
    });
  },
  resetLessonPreQuiz: lessonId => {
    set(state => {
      const current = state.LESSON[lessonId];
      if (!current) {
        return state;
      }
      return {
        LESSON: Object.assign({}, state.LESSON, {
          [lessonId]: Object.assign({}, current, {
            questionIndex: 0,
            correctAnswers: 0,
            perContentScores: {},
            quizStarted: true,
            isReviewingAnswer: false,
            shuffledSelections: undefined
          })
        })
      };
    });
  },
  setPreQuizTask: ({
    lessonId,
    taskId,
    videoContentId
  }) => {
    set(state => {
      const current = state.LESSON[lessonId];
      return {
        LESSON: Object.assign({}, state.LESSON, {
          [lessonId]: Object.assign({}, current, {
            tasks: Object.assign({}, (current === null || current === void 0 ? void 0 : current.tasks) || {}, {
              [videoContentId]: taskId
            })
          })
        })
      };
    });
  },
  setPreQuizTasks: ({
    lessonId,
    tasks
  }) => {
    set(state => {
      const current = state.LESSON[lessonId];
      if (!current) {
        return state;
      }
      return {
        LESSON: Object.assign({}, state.LESSON, {
          [lessonId]: Object.assign({}, current, {
            tasks
          })
        })
      };
    });
  },
  setPreQuizStatus: ({
    lessonId,
    status
  }) => {
    set(state => {
      const current = state.LESSON[lessonId];
      if (!current) {
        return state;
      }
      return {
        LESSON: Object.assign({}, state.LESSON, {
          [lessonId]: Object.assign({}, current, {
            generationStatus: status
          })
        })
      };
    });
  },
  setPreQuizTotalQuestions: ({
    lessonId,
    totalQuestions
  }) => {
    set(state => {
      const current = state.LESSON[lessonId];
      if (!current) {
        return state;
      }
      return {
        LESSON: Object.assign({}, state.LESSON, {
          [lessonId]: Object.assign({}, current, {
            totalQuestions
          })
        })
      };
    });
  },
  setShuffledSelections: ({
    lessonId,
    shuffledSelections
  }) => {
    set(state => {
      const current = state.LESSON[lessonId];
      if (!current) {
        return state;
      }
      return {
        LESSON: Object.assign({}, state.LESSON, {
          [lessonId]: Object.assign({}, current, {
            shuffledSelections
          })
        })
      };
    });
  },
  startPreQuiz: lessonId => {
    set(state => {
      const current = state.LESSON[lessonId];
      if (current) {
        return {
          LESSON: Object.assign({}, state.LESSON, {
            [lessonId]: Object.assign({}, current, {
              quizStarted: true
            })
          })
        };
      }
      return {
        LESSON: Object.assign({}, state.LESSON, {
          [lessonId]: {
            questionIndex: 0,
            correctAnswers: 0,
            totalQuestions: 0,
            perContentScores: {},
            tasks: {},
            generationStatus: 'PENDING',
            quizStarted: true,
            isReviewingAnswer: false
          }
        })
      };
    });
  },
  clearPreQuizTasks: lessonId => {
    set(state => {
      const current = state.LESSON[lessonId];
      if (!current) {
        return state;
      }
      return {
        LESSON: Object.assign({}, state.LESSON, {
          [lessonId]: Object.assign({}, current, {
            tasks: {},
            generationStatus: 'PENDING',
            shuffledSelections: undefined
          })
        })
      };
    });
  },
  confirmQuestionReviewed: lessonId => {
    set(state => {
      const current = state.LESSON[lessonId];
      if (!current) {
        return state;
      }
      return {
        LESSON: Object.assign({}, state.LESSON, {
          [lessonId]: Object.assign({}, current, {
            isReviewingAnswer: false
          })
        })
      };
    });
  }
}));
export const resetLessonPreQuizStore = () => useLessonPreQuizStore.setState(LessonPreQuizDefaultState);
export const useLessonPreQuizSession = () => {
  const {
    LESSON,
    initializeLessonPreQuiz,
    incrementLessonPreQuizQuestion,
    resetLessonPreQuiz,
    setPreQuizTask,
    setPreQuizTasks,
    setPreQuizStatus,
    setPreQuizTotalQuestions,
    setShuffledSelections,
    startPreQuiz,
    clearPreQuizTasks,
    confirmQuestionReviewed
  } = useLessonPreQuizStore();
  const getLessonPreQuizState = useCallback(lessonId => LESSON[lessonId], [LESSON]);
  const getContentScore = useCallback((lessonId, contentId) => {
    var _LESSON$lessonId;
    return (_LESSON$lessonId = LESSON[lessonId]) === null || _LESSON$lessonId === void 0 ? void 0 : _LESSON$lessonId.perContentScores[contentId];
  }, [LESSON]);
  const getPreQuizStatus = useCallback(lessonId => {
    var _LESSON$lessonId2;
    return (_LESSON$lessonId2 = LESSON[lessonId]) === null || _LESSON$lessonId2 === void 0 ? void 0 : _LESSON$lessonId2.generationStatus;
  }, [LESSON]);
  const getShuffledSelections = useCallback(lessonId => {
    var _LESSON$lessonId3;
    return (_LESSON$lessonId3 = LESSON[lessonId]) === null || _LESSON$lessonId3 === void 0 ? void 0 : _LESSON$lessonId3.shuffledSelections;
  }, [LESSON]);
  return {
    getLessonPreQuizState,
    initializeLessonPreQuiz,
    incrementLessonPreQuizQuestion,
    resetLessonPreQuiz,
    getContentScore,
    setPreQuizTask,
    setPreQuizTasks,
    setPreQuizStatus,
    setPreQuizTotalQuestions,
    setShuffledSelections,
    startPreQuiz,
    clearPreQuizTasks,
    getPreQuizStatus,
    getShuffledSelections,
    confirmQuestionReviewed
  };
};